/**
 * Speed/wind-driven GRADE demotion rules, mirroring server_stream/controllers/gradeRules.js.
 *
 * The stream server already grades every sample it processes; this copy is the backstop that
 * re-applies the same rules to rows read back from storage that were graded before a rule existed.
 * The two must agree, so the constants live in exactly these two files and nowhere else —
 * frontend/__tests__/unit/liveGradeRules.test.ts fails if they drift apart.
 *
 * Why a rule that ignores wind is needed at all: the live GRADE is fail-open (the processor starts
 * every sample at 3 and only demotes), so a demotion rule that needs TWS present cannot see a boat
 * parked on the dock with its TM feed down. See the module header on the server side.
 */

/** GRADE forced to 1 below this boat speed, whatever the wind is doing. 0 disables the rule. */
export const GRADE1_NOT_MOVING_BSP_KPH_LT = 5;

/** The low-boat-speed-in-breeze rule only applies above this true wind speed. */
export const GRADE1_LOW_BSP_TWS_KPH_GT = 15;

/** GRADE forced to 1 when TWS clears the gate above and |Bsp_kph| is below this. */
export const GRADE1_LOW_BSP_ABS_KPH_LT = 28;

/** How stale a carried-forward BSP/TWS sample may be and still grade the current one. */
export const GRADE_INPUT_MAX_AGE_MS = 2000;

function finite(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

/**
 * Demote `grade` to 1 when boat speed says the sample is not usable. Never invents a grade: a null
 * grade stays null, and grades already at 1 (or 0 = excluded) pass through untouched.
 */
export function applyLowSpeedGradeRules(
  grade: number | null,
  bspKph: number | null,
  twsKph: number | null,
): number | null {
  const g = finite(grade);
  if (g === null || g <= 1) {
    return grade;
  }

  const bsp = finite(bspKph);
  const tws = finite(twsKph);

  if (bsp !== null && GRADE1_NOT_MOVING_BSP_KPH_LT > 0 && Math.abs(bsp) < GRADE1_NOT_MOVING_BSP_KPH_LT) {
    return 1;
  }

  if (
    bsp !== null &&
    tws !== null &&
    Math.abs(tws) > GRADE1_LOW_BSP_TWS_KPH_GT &&
    Math.abs(bsp) < GRADE1_LOW_BSP_ABS_KPH_LT
  ) {
    return 1;
  }

  return g;
}
